import {eventWithTime} from '@rrweb/types';

import {GenerateSignedReplayParamsResponse} from 'app_api/projects/replays/types';
import {TranslationKey} from 'common/i18n';
import {FilterClause} from 'common/report/insights/models/filter-clause';
import {ResourceType} from 'common/report/constants';
import {FiltersOperator, SelectorTypeDefinitions} from 'common/query-builder/query-entry-section/types';

import {HeatMapClickEvent, HeatMapElementPlacement, HeatMapPlayerErrorState} from '../heat-maps/types';

export type RecordingMetadata = {
  replayId: string;
  time: number;
  distinctId: string;
  name: string;
  avatarUrl: string;
  email: string;
  duration: number;
  replayEnv: string;
  browser: string;
  os: string;
  source: string; // Tells us which SDK the recording came from
  startingURL: string;
  retentionPeriod: number | null;
  /**
   * When viewing replays from an event, indicates whether the replay is "stitched" or not.
   * A stitched replay means we inferred an event occurred in this replay via distinct_id and timestamp,
   * but the event itself does not have the $mp_replay_id property.
   */
  isStitchedFromEvent: boolean;
};

export type RecordingPreviewMetadata = {
  replayId: string;
  eventSelectionTime: number;
};

export type RecordingEventMetadata = {
  eventName: string;
  eventTime: number;
  key: string;
  mpEventName: string;
  quantity: number;

  /**
   * This event has a $mp_replay_id property that matches the replayId of the recording.
   * i.e. this event was guaranteed "observed" in the replay.
   */
  hasMatchingReplayIdProperty: boolean;

  propertyMapping: Map<string, string>;
};

export type RecordingPageViewEvents = {
  replayId: string;
  replayPageViewEvents: RecordingPageViewEventMetadata[];
};

export type RecordingPageViewEventMetadata = {
  currentURL: string;
  eventTime: number;
};

export type RecordingEvents = {
  replayId: string;
  replayEvents: RecordingEventMetadata[];
};

export type RecordingInactivePeriod = {
  startTimeMs: number;
  endTimeMs: number;
};

export enum RecordingPlayerTrackingEvent {
  AiSummaryTimestampClick = `[Recording Player] AI Summary Timestamp Click`,
  AiSummaryToggle = `[Recording Player] AI Summary Toggle`,
  Close = `[Recording Player] Modal Closed`,
  CopyLink = `[Recording Player] Copy Link`,
  Feedback = `[Recording Player] Feedback Submitted`,
  Finish = `[Recording Player] Replay Finished`,
  ListLoaded = `[Recording Player] Replay List Loaded`,
  ListLoadFailed = `[Recording Player] Replay List Load Failed`,
  LoadMoreReplays = `[Recording Player] Load More Replays`,
  Open = `[Recording Player] Modal Opened`,
  ReplayLoaded = `[Recording Player] Replay Data Loaded`,
  Sort = `[Recording Player] Replay List Sorted`,
  ToggleAutoPlay = `[Recording Player] Toggle Auto Play`,
  ToggleSkipInactivity = `[Recording Player] Toggle Skip Inactivity`,
  ToggleStitching = `[Recording Player] Toggle Stitching`,
  Watch = `[Recording Player] Replay Watched`,
}

export enum RecordingPlayerSource {
  UsersProfile = `User Profile`,
  UsersProfileLink = `User Profile Link`,
  Funnels = `Funnels`,
  Event = `Event`,
  Cohort = `Cohort`,
  InsightsProfile = `InsightsProfile`,
  Home = `Home`,
  HeatMaps = `Heat Maps`,
  Lexicon = `Lexicon`,
  QueryPreview = `Query Preview`,
  ReplayHome = `Replay Home`,
  EventsFeed = `Events Feed`,
}

export enum RecordingSpeed {
  Half = 0.5,
  ThreeQuarters = 0.75,
  Normal = 1,
  Double = 2,
  Quadruple = 4,
  Octuple = 8,
  Dectuple = 10,
}

export enum TimeDisplaySetting {
  Hours = `hours`,
  Minutes = `minutes`,
  Seconds = `seconds`,
}

export enum WindowMessageRecipient {
  ParentApp,
  ChildReplay,
}

export type SignedReplayParams = Required<GenerateSignedReplayParamsResponse>[`results`];

export type ParentAppMessagePayload = {
  embedReady?: true;
  embedBlocked?: true;
  error?: string;
  loadingRecording?: boolean;
  playerClicked?: true;
  recordingDuration?: number;
  recordingFinished?: boolean;
  recordingInactivePeriods?: RecordingInactivePeriod[];
  recordingProgress?: number;
  recordingSkippingInactive?: boolean;
  recordingSpeed?: RecordingSpeed;
  recordingStarted?: boolean;
  recordingStartTime?: number;
  recordingPaused?: boolean;
  elementPositionsFound?: HeatMapElementPlacement[];
  trackingData?: Obj;
  pageloadEvents?: eventWithTime[];
  trackReplayLoaded?: Obj;
  playerInstanceId?: string;
  isSkippingInactivity?: boolean;
};

/**
 * All the authenticated params and metadata needed for the embedded player to be able to pull replay files.
 * This should be the minimum set of data needed since it will be passed via postMessage (so no PII to be safe).
 */
export type EmbedReplayParams = {
  signedParams: SignedReplayParams;
  // checkpoint start time used to optionally load multiple recordings in order
  checkpointStartTime?: number;
  // retention period of the recording (in days)
  retentionPeriod: number;
  replayId: string;
};

/**
 * All the data needed for an embed to be able to load and display one or more replays
 */
export type LoadEmbedParams = {
  replayParams: EmbedReplayParams[];
  // target origin that the embedded player should use to send messages to the parent
  targetOrigin: string;
  heatMapParams?: {
    progress: number;
  };
};

export enum ReplayErrorStateType {
  NoReplaysAvailable = `no_replay_available`,
  LoadError = `load_error`,
  NotIntegrated = `replays_not_integrated`,
}

export enum EmbedErrorStateType {
  NoReplayFound = `no_replay_found`,
  EmbedBlocked = `embed_blocked`,
}

export type HeatMapPlayerErrorStateType = HeatMapPlayerErrorState | EmbedErrorStateType;

export type EmbedReplayPayload = {
  embedParams?: LoadEmbedParams;
  recordingPaused?: boolean;
  recordingProgress?: number;
  recordingSpeed?: RecordingSpeed;
  targetOrigin?: string;
  isDarkMode?: boolean;
  heatMapData?: Map<string, HeatMapClickEvent[]>;
  heatMapActive?: boolean;
  playerInstanceId?: string;
  isSkipInactivityEnabled?: boolean;
};

export type ParentAppReplayMessage = {
  recipient: WindowMessageRecipient.ParentApp;
  payload: ParentAppMessagePayload;
};

export type EmbedReplayMessage = {
  recipient: WindowMessageRecipient.ChildReplay;
  payload: EmbedReplayPayload;
};

export type ReplayMessage = EmbedReplayMessage | ParentAppReplayMessage;

export function isReplayWindowMessage(event: MessageEvent): event is MessageEvent<ReplayMessage> {
  return event.data?.recipient !== undefined;
}

export enum SortOption {
  MostRecent = `MostRecent`,
  LeastRecent = `LeastRecent`,
  LongestDuration = `LongestDuration`,
  ShortestDuration = `ShortestDuration`,
  MostEvents = `MostEvents`,
  LeastEvents = `LeastEvents`,
}

// TODO: Remove once playlist header is shown for both modal and sr home
export const DeprecatedSortOptionTranslationLabels: Record<SortOption | string, TranslationKey> = {
  [SortOption.MostRecent]: `session-replay.sort.most-recent`,
  [SortOption.LeastRecent]: `session-replay.sort.least-recent`,
  [SortOption.LongestDuration]: `session-replay.sort.longest-duration`,
  [SortOption.ShortestDuration]: `session-replay.sort.shortest-duration`,
  [SortOption.MostEvents]: `session-replay.sort.most-events`,
  [SortOption.LeastEvents]: `session-replay.sort.least-events`,
};

export const SortOptionTranslationLabels: Record<SortOption | string, TranslationKey> = {
  [SortOption.MostRecent]: `session-replay.sort.recency`,
  [SortOption.LeastRecent]: `session-replay.sort.recency`,
  [SortOption.LongestDuration]: `session-replay.sort.duration`,
  [SortOption.ShortestDuration]: `session-replay.sort.duration`,
  [SortOption.MostEvents]: `session-replay.sort.activity`,
  [SortOption.LeastEvents]: `session-replay.sort.activity`,
};

export type LatestRecordingData = {
  recordings: RecordingMetadata[];
  segmentationLimitReached: boolean;
};

export type EventRecordingData = {
  recordings: RecordingMetadata[];
  segmentationLimitReached?: boolean;
  targetEventMapping: RecordingEvents[];
  numDuplicateRecordingsForEvent: number;
  replayCustomPropIds: string[];
};

export type EventFilterInfo = {
  name: string;
  filters: FilterClause[];
  filterOp: FiltersOperator;
};

export type FilterEntry = {
  type: ResourceType.Event | SelectorTypeDefinitions.Filter;
  event: any;
  filters: any[];
  filtersOperator: FiltersOperator;
};

export type KeyAction = {
  timestamp: number;
  description: string;
};

export type AiSummaryResponse = {
  summary: string;
  key_actions: KeyAction[];
  duration_estimate: string;
  events_processed: number;
};

export enum EventType {
  RageClick = `rage_click`,
  DeadClick = `dead_click`,
  Unknown = `unknown`,
}

export enum TimelineType {
  Main = `main`,
  Insightful = `insightful`,
  Stitched = `stitched`,
}
